import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircle } from "lucide-react";
import useSEO from "@/hooks/useSEO";

export function LegalShell({ title, updated, children, path }) {
  useSEO({
    title: `${title} · Instant Site Revival`,
    description: `${title} for Instant Site Revival — modern websites, social media care, and AI avatar services for local businesses.`,
    path: path,
  });
  return (
    <div data-testid="legal-page" className="min-h-screen bg-slate-50">
      <header className="bg-[#0B0F19] text-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="legal-back-home" className="inline-flex items-center gap-2 text-slate-200 hover:text-white text-sm font-bold">
            <ArrowLeft size={16} /> Back to home
          </Link>
          <a
            href="https://wa.me/17865756919"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1eb558] text-white font-bold px-3.5 py-1.5 text-xs"
          >
            <MessageCircle size={14} /> Message Us
          </a>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-5 lg:px-8 py-16">
        <div className="text-xs font-black uppercase tracking-widest text-blue-600">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 mt-2">{title}</h1>
        <p className="text-sm text-slate-500 mt-2">Last updated: {updated}</p>
        <div className="mt-10 space-y-4 text-slate-700 leading-relaxed [&_h2]:font-display [&_h2]:font-extrabold [&_h2]:text-2xl [&_h2]:text-slate-900 [&_h2]:mt-10 [&_h2]:mb-3 [&_p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mt-1 [&_a]:text-blue-600 [&_a:hover]:underline [&_strong]:text-slate-900 [&_strong]:font-semibold">
          {children}
        </div>
      </main>
    </div>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service" updated="December 2025" path="/terms">
      <p>
        These Terms of Service (“Terms”) govern your use of the Instant Site Revival website
        design and management services (“Services”) provided by Instant Site Revival (“we”, “us”,
        “our”). By purchasing a website build, subscribing to the Website Care &amp; Hosting Plan,
        or otherwise engaging us, you agree to these Terms.
      </p>

      <h2>1. Website Build</h2>
      <p>
        The one-time Website Build fee (regularly $1,499, discounted to $750 during limited-time
        promotions) covers the design and development of a professional website as described on
        our offer page, including reasonable design and content revisions before final approval.
      </p>
      <p>
        “Reasonable revisions” means standard design and content changes to the agreed scope. It
        does not include full redesigns after approval, changes to the business model, or large
        new features — those may require a separate quote.
      </p>

      <h2>2. Same-Day Launch</h2>
      <p>
        Same-day launch is available after you approve the website concept and provide all
        required business information and domain access. Launch timing depends on the complexity
        of any requested changes, the availability of business assets, domain access, and
        third-party services outside our control.
      </p>

      <h2>3. Website Care &amp; Hosting Plan ($99/month)</h2>
      <ul>
        <li>Billed monthly in advance, no long-term contract.</li>
        <li>Includes hosting, SSL/security basics, uptime support, minor updates, and standard content changes (text, images, services, hours, contact info).</li>
        <li>Approved standard updates are completed within 24 business hours of receipt.</li>
        <li>Large redesigns, new pages, advanced features, e-commerce systems, booking systems, custom integrations, or major rebuilds are not included and may require a separate quote.</li>
        <li>You may cancel at any time by contacting us. Upon cancellation, hosting and ongoing support stop at the end of the current billing cycle; no refunds are issued for the current month.</li>
      </ul>

      <h2>4. Ownership</h2>
      <p>
        <strong>You own your website.</strong> The design, content, and code produced as part of
        your build belong to you. If you cancel the Care &amp; Hosting Plan, we can provide a
        copy of your website files or assist with a transfer to another host, subject to a
        reasonable handoff process.
      </p>

      <h2>5. Payments &amp; Refunds</h2>
      <p>
        Build fees are due before we begin final production after concept approval. Monthly Care
        &amp; Hosting fees are charged in advance each month. Due to the custom nature of our
        work, website build fees are non-refundable once production has started. If you are
        unhappy before production starts, contact us and we will work to make it right.
      </p>

      <h2>6. Third-Party Costs</h2>
      <p>
        Some items are not included in our fees and are billed directly by third parties or by
        us as pass-through costs: domain purchase/renewal, paid apps or plugins, advanced
        e-commerce platforms, booking systems, logo design, professional photography or video,
        and paid advertising.
      </p>

      <h2>7. Acceptable Use</h2>
      <p>
        You agree not to use your website to publish unlawful, defamatory, infringing, or harmful
        content. We may suspend services for material violations.
      </p>

      <h2>8. Limitation of Liability</h2>
      <p>
        Our total liability for any claim arising out of the Services is limited to the amount
        you paid us in the 3 months preceding the claim. We do not guarantee specific business
        results, sales, leads, or search rankings.
      </p>

      <h2>9. Contact</h2>
      <p>
        Questions? Reach us at{" "}
        <a href="mailto:info@instantsiterevival.com" className="text-blue-600">info@instantsiterevival.com</a>{" "}
        or WhatsApp <a href="https://wa.me/17865756919" className="text-blue-600">786-575-6919</a>.
      </p>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy" updated="December 2025" path="/privacy">
      <p>
        This Privacy Policy explains how Instant Site Revival (“we”, “us”) collects and uses
        information when you visit InstantSiteRevival.com or use our services.
      </p>

      <h2>1. What we collect</h2>
      <ul>
        <li><strong>Contact form submissions</strong>: name, business name, phone, email, current website URL, the kind of website you need, and any message you send. Submitted voluntarily.</li>
        <li><strong>Basic analytics</strong>: standard web usage data such as pages visited, approximate location, and device type.</li>
      </ul>

      <h2>2. How we use it</h2>
      <ul>
        <li>To reply to your inquiry and deliver the services you’ve requested.</li>
        <li>To send service-related communications (order updates, renewal notices, etc.).</li>
        <li>To improve our website and offers.</li>
      </ul>

      <h2>3. How we share it</h2>
      <p>
        We do not sell your personal information. We use reputable service providers to operate
        our business (e.g., email delivery via Resend, hosting, analytics). These providers only
        process data on our behalf and are bound by confidentiality obligations.
      </p>

      <h2>4. Your choices</h2>
      <ul>
        <li>You can ask us to delete your personal information at any time — email <a href="mailto:info@instantsiterevival.com" className="text-blue-600">info@instantsiterevival.com</a>.</li>
        <li>You can opt out of marketing emails using the unsubscribe link in any such email.</li>
      </ul>

      <h2>5. Security</h2>
      <p>
        We use industry-standard practices to protect your information. No method of transmission
        over the internet is 100% secure, but we work to keep your information safe.
      </p>

      <h2>6. Children</h2>
      <p>Our services are not directed to children under 13.</p>

      <h2>7. Updates</h2>
      <p>
        We may update this policy from time to time. The “Last updated” date above reflects the
        most recent change.
      </p>

      <h2>8. Contact</h2>
      <p>
        Questions about this policy? Email{" "}
        <a href="mailto:info@instantsiterevival.com" className="text-blue-600">info@instantsiterevival.com</a>.
      </p>
    </LegalShell>
  );
}
